import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { escalationPolicyService } from '@/services/escalation-policy.service';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import type { EscalationPolicy } from '@/modules/escalation-policy/escalation-policy';

// Keyed by brand, as every brand setting: two brands must never read each other's policy.
const escalationPolicyQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'escalation-policy', buScopeKey(get)],
  queryFn: () => escalationPolicyService.get(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

const helpdeskAgentsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'helpdesk-agents', buScopeKey(get)],
  queryFn: () => escalationPolicyService.helpdeskAgents(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

const updateEscalationPolicyMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'escalation-policy', 'update'],
  mutationFn: (policy: EscalationPolicy) => escalationPolicyService.update(policy, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['brand', 'escalation-policy']);
  }
}));

export function useEscalationPolicy() {
  const [query] = useAtom(escalationPolicyQueryAtom);
  return query;
}

/** The brand's external helpdesk agents: who Assign to User can assign a ticket to. */
export function useHelpdeskAgents() {
  const [query] = useAtom(helpdeskAgentsQueryAtom);
  return query;
}

export function useUpdateEscalationPolicy() {
  const [mutation] = useAtom(updateEscalationPolicyMutationAtom);
  return mutation;
}
